import { Box, Typography, useTheme, alpha, CircularProgress } from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";
import type { AxisValueFormatterContext } from "@mui/x-charts/models";
import DashboardPaper from "../../common/DashboardPaper";
import { formatAxisDate } from "../../../utils/chartDate";
import type { DeflectionsChartData } from "../../../types/deflections";

interface Props {
  data?: DeflectionsChartData;
  loading?: boolean;
}

export default function ApiCallsChart({ data, loading }: Props) {
  const theme = useTheme();
  const total = (data?.series ?? []).reduce(
    (sum, s) => sum + s.data.reduce((a, b) => a + b, 0),
    0,
  );
  const colors = [theme.palette.primary.main, alpha(theme.palette.primary.main, 0.45)];

  return (
    <DashboardPaper sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
        <Box>
          <Typography variant="h6" fontWeight={600}>
            Order Insight &amp; Digital Receipt Requests
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Inbound lookups from issuers (OI) and in-app lookups (Digital Receipts) that Chargemont responded to.
          </Typography>
        </Box>
        <Box textAlign="right">
          <Typography variant="caption" color="text.secondary">TOTAL</Typography>
          <Typography variant="h6" fontWeight={700}>{total.toLocaleString()}</Typography>
        </Box>
      </Box>
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
          <CircularProgress />
        </Box>
      ) : data && total > 0 ? (
        <BarChart
          xAxis={[
            {
              data: data.xAxis,
              valueFormatter: (value: string, ctx: AxisValueFormatterContext) => formatAxisDate(value, ctx.location),
              tickLabelStyle: { fontSize: 11, fill: theme.palette.text.secondary },
            },
          ]}
          yAxis={[{ tickLabelStyle: { fontSize: 12, fill: theme.palette.text.secondary } }]}
          series={data.series.map((s, i) => ({
            data: s.data,
            label: s.label,
            color: colors[i % colors.length],
            stack: "total",
          }))}
          height={320}
          borderRadius={3}
          slotProps={{
            legend: {
              direction: "horizontal",
              position: { vertical: "bottom", horizontal: "center" },
              sx: { fontSize: 11 },
            },
          }}
          margin={{ bottom: 60 }}
        />
      ) : (
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 300 }}>
          <Typography color="text.secondary">No data available</Typography>
        </Box>
      )}
    </DashboardPaper>
  );
}
